import { useEffect, useRef, useState } from 'react';

const VenueSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Image */}
          <div 
            className={`relative transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="w-full h-[500px] relative">
                <img
                  src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/ee5df52e9993e9fffff882b39c555979.jpeg"
                  alt="İstanbul Atlas Üniversitesi Kongre Merkezi"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent"></div>
              
              {/* Floating Badge */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                      <i className="ri-map-pin-line text-accent text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary">İstanbul Atlas Üniversitesi Kongre Merkezi</h3>
                      <p className="text-gray-600 text-sm">İstanbul, Türkiye</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div 
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
              <i className="ri-calendar-event-line text-accent"></i>
              <span className="text-accent font-semibold text-sm">Kongre Yeri ve Tarihi</span>
            </div>

            <h2 className="font-display text-4xl font-bold text-primary mb-6 leading-tight">
              Kongre Mekanı
            </h2>

            <div className="space-y-6">
              <div 
                className={`flex items-start space-x-4 transition-all duration-700 delay-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-primary text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-primary mb-2">Kongre Merkezi</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-primary">Genç Diş Hekimliği Derneği 2. Ulusal İstanbul Kongresi</strong>, 02 – 03 Mayıs 2026 tarihinde{' '}
                    <strong className="text-primary">İstanbul Atlas Üniversitesi Kongre Merkezi – İstanbul</strong>'da gerçekleştirilecektir.
                  </p>
                </div>
              </div>

              <div 
                className={`flex items-start space-x-4 transition-all duration-700 delay-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-2-line text-accent text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-primary mb-2">Tarih</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-accent">02 – 03 Mayıs 2026</strong>
                  </p>
                </div>
              </div>

              <div 
                className={`flex items-start space-x-4 transition-all duration-700 delay-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-sun-line text-yellow-500 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-primary mb-2">İklim Bilgisi</h3>
                  <p className="text-gray-700 leading-relaxed">
                    02 – 03 Mayıs 2026 tarihinde İstanbul'da ortalama hava sıcaklığı gündüz{' '}
                    <strong className="text-primary">18°C</strong> olup hava açık ve güneşlidir.
                  </p>
                </div>
              </div>
            </div>

            {/* Map Button */}
            <div 
              className={`mt-8 transition-all duration-700 delay-900 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <a
                href="https://www.google.com/maps/search/İstanbul+Atlas+Üniversitesi+Kongre+Merkezi"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line text-xl"></i>
                <span>Haritada Görüntüle</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueSection;